import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Collapse,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../Iconify';
import AssignedToLead from '../AssignedToLead';
import { dispatch, useSelector } from '../../redux/store';
import { createTask } from '../../redux/slices/taskSlice';
import { updateError } from '../../redux/slices/user';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { toDatetimeLocalString } from '../../utils/taskDateUtils';
import { TASK_PRIORITY, TASK_CATEGORY, TASK_METHOD } from '../../constants/TaskConstants';
import capitalizeFirstLetter from '../../utils/capitalizedFunc';

// ─── Date chip helpers ────────────────────────────────────────────────────────
// Mirrors the Today/Tomorrow/Next-week pattern in CreateTaskDialog.jsx.

const getToday = () => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return toDatetimeLocalString(d);
};

const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return toDatetimeLocalString(d);
};

const getNextWeek = () => {
    const d = new Date();
    const daysUntilMon = ((8 - d.getDay()) % 7) || 7;
    d.setDate(d.getDate() + daysUntilMon);
    d.setHours(9, 0, 0, 0);
    return toDatetimeLocalString(d);
};

const DATE_CHIPS = [
    { label: 'Today', getValue: getToday },
    { label: 'Tomorrow', getValue: getTomorrow },
    { label: 'Next week', getValue: getNextWeek },
    { label: 'Custom', getValue: null },
];

// ─── Method chip options ──────────────────────────────────────────────────────
const STATIC_METHODS = [
    { label: 'Call', value: TASK_METHOD.CALL, icon: 'solar:phone-bold' },
    { label: 'WhatsApp', value: TASK_METHOD.WHATSAPP, icon: 'logos:whatsapp-icon' },
    { label: 'Email', value: TASK_METHOD.EMAIL, icon: 'eva:email-fill' },
    { label: 'Other', value: TASK_METHOD.OTHER, icon: 'eva:more-horizontal-fill' },
];

// ─── Shared chip style factory ────────────────────────────────────────────────
const chipSx = (active) => ({
    px: 1.5,
    py: 0.5,
    borderRadius: '8px',
    border: '1px solid',
    borderColor: active ? '#00AB55' : 'rgba(0,0,0,0.12)',
    bgcolor: active ? 'rgba(0,171,85,0.08)' : '#ffffff',
    color: active ? '#00AB55' : '#6e6e73',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    transition: 'all 150ms ease',
    '&:hover': {
        borderColor: '#00AB55',
        color: '#00AB55',
        bgcolor: 'rgba(0,171,85,0.06)',
    },
});

// ─── Component ────────────────────────────────────────────────────────────────

const ScheduleTaskReminder = ({
    practiceId,
    leadId,
    leadDetail,
    onTaskActivity,
    handleClose,
    handleReloadLeads,
}) => {
    const { reminderMethod: schemaReminderMethod } = useSelector((state) => state.schema);
    const { currentTreatmentDataGlobal } = useSelector((state) => state.multiTreatment);

    // ── WHEN ──────────────────────────────────────────────────────────────────
    const [dateChip, setDateChip] = useState('Tomorrow');
    const [dueAt, setDueAt] = useState(() => getTomorrow());

    const handleDateChip = (chip) => {
        setDateChip(chip.label);
        if (chip.getValue) {
            setDueAt(chip.getValue());
        }
        // Custom: keep the existing value in the datetime field; user edits it.
    };

    // ── HOW ───────────────────────────────────────────────────────────────────
    // Use schema-sourced methods when available (same shape as AddTaskReminders),
    // otherwise fall back to the static set above. Schema enum values are
    // UPPERCASE ('CALL'); the payload + TASK_METHOD defaults are lowercase
    // ('call'), so normalize schema values to lowercase to keep the default
    // chip selected and the payload `method` lowercase. STATIC_METHODS is
    // already lowercase.
    const methodOptions = schemaReminderMethod?.length
        ? schemaReminderMethod.map((m) => ({ ...m, value: String(m.value).toLowerCase() }))
        : STATIC_METHODS;
    const [selectedMethod, setSelectedMethod] = useState(TASK_METHOD.CALL);

    // ── ASSIGN TO ─────────────────────────────────────────────────────────────
    const [assignees, setAssignees] = useState([]);

    // ── NOTE ──────────────────────────────────────────────────────────────────
    const [note, setNote] = useState('');

    // ── MORE OPTIONS ──────────────────────────────────────────────────────────
    const [moreOpen, setMoreOpen] = useState(false);
    const leadFirstName = capitalizeFirstLetter(leadDetail?.first_name || '');
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState(TASK_CATEGORY.FOLLOW_UP);
    const [priority, setPriority] = useState(TASK_PRIORITY.NORMAL);

    // ── Saving state ──────────────────────────────────────────────────────────
    const [saving, setSaving] = useState(false);

    // ── Guards ────────────────────────────────────────────────────────────────
    if (!hasFeatureAccess('tasks-v2', practiceId)) return null;
    if (!leadId) return null;

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSchedule = async () => {
        if (!dueAt) {
            dispatch(updateError({ success: false, message: 'Please choose a date and time.' }));
            return;
        }

        // Convert the datetime-local string (YYYY-MM-DDTHH:mm) to a Date, then
        // produce a proper ISO 8601 string with local offset for the backend.
        const localDate = new Date(dueAt);
        if (Number.isNaN(localDate.getTime())) {
            dispatch(updateError({ success: false, message: 'Invalid date. Please pick again.' }));
            return;
        }

        const resolvedTitle = title.trim() || (leadFirstName ? `Follow-up: ${leadFirstName}` : 'Follow-up');

        const leadTreatmentId =
            currentTreatmentDataGlobal?.treatmentData?.id ||
            leadDetail?.lead_treatments?.[0]?.id ||
            null;

        const payload = {
            title: resolvedTitle,
            ...(note.trim() && { description: note.trim() }),
            due_at: localDate.toISOString(), // eslint-disable-line camelcase
            priority,
            category,
            ...(selectedMethod && { method: selectedMethod }),
            assignees: assignees.map((a) => ({ practice_user_id: a.id })), // eslint-disable-line camelcase
            lead: leadId,
            ...(leadTreatmentId && { lead_treatment: leadTreatmentId }), // eslint-disable-line camelcase
        };

        setSaving(true);
        dispatch(
            createTask(practiceId, payload, (result) => {
                setSaving(false);
                if (result?.success) {
                    if (onTaskActivity) onTaskActivity();
                    if (handleClose) handleClose();
                    if (handleReloadLeads) handleReloadLeads();
                }
            })
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 2.5, bgcolor: '#ffffff', borderRadius: '12px' }}>
            {/* Header */}
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.015em', mb: 0.25 }}>
                Schedule a follow-up
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6e6e73', mb: 2.5 }}>
                Create a task reminder for this lead.
            </Typography>

            {/* WHEN ─────────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                    When
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75, mb: dateChip === 'Custom' ? 1.5 : 0 }}>
                    {DATE_CHIPS.map((chip) => (
                        <Box
                            key={chip.label}
                            onClick={() => handleDateChip(chip)}
                            sx={chipSx(dateChip === chip.label)}
                        >
                            {chip.label}
                        </Box>
                    ))}
                </Stack>
                {dateChip === 'Custom' && (
                    <TextField
                        type="datetime-local"
                        value={dueAt}
                        onChange={(e) => setDueAt(e.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: toDatetimeLocalString(new Date()) }}
                        sx={{ mt: 1 }}
                    />
                )}
            </Box>

            {/* HOW ──────────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                    How
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
                    {methodOptions.map((m) => (
                        <Box
                            key={m.value}
                            onClick={() => setSelectedMethod(m.value === selectedMethod ? null : m.value)}
                            sx={chipSx(selectedMethod === m.value)}
                        >
                            {m.icon && (
                                <Iconify
                                    icon={m.icon}
                                    width={13}
                                    height={13}
                                    sx={{ color: selectedMethod === m.value ? '#00AB55' : '#6e6e73' }}
                                />
                            )}
                            {m.label}
                        </Box>
                    ))}
                </Stack>
            </Box>

            {/* ASSIGN TO ────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2.5 }}>
                <AssignedToLead
                    label="ASSIGN TO"
                    assignToState={assignees}
                    setAssignTo={setAssignees}
                    staffRole="assignee"
                />
            </Box>

            {/* NOTE ─────────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                    Note (optional)
                </Typography>
                <TextField
                    multiline
                    minRows={2}
                    maxRows={5}
                    fullWidth
                    size="small"
                    placeholder="Add a note…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
            </Box>

            {/* MORE OPTIONS ─────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2.5 }}>
                <Button
                    size="small"
                    onClick={() => setMoreOpen(!moreOpen)}
                    endIcon={<Iconify icon={moreOpen ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={14} height={14} />}
                    sx={{
                        color: '#0071e3',
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'none',
                        letterSpacing: '-0.005em',
                        px: 0,
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                    }}
                >
                    More options
                </Button>
                <Collapse in={moreOpen} timeout={250}>
                    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                        {/* Title */}
                        <Box>
                            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                                Title
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={leadFirstName ? `Follow-up: ${leadFirstName}` : 'Follow-up'}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </Box>
                        {/* Category */}
                        <Box>
                            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                                Category
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
                                {[
                                    { label: 'Follow Up', value: TASK_CATEGORY.FOLLOW_UP },
                                    { label: 'Patient Care', value: TASK_CATEGORY.PATIENT_CARE },
                                    { label: 'Lead & Marketing', value: TASK_CATEGORY.LEAD_MARKETING },
                                    { label: 'Operational', value: TASK_CATEGORY.OPERATIONAL },
                                ].map((opt) => (
                                    <Box key={opt.value} onClick={() => setCategory(opt.value)} sx={chipSx(category === opt.value)}>
                                        {opt.label}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                        {/* Priority */}
                        <Box>
                            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                                Priority
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
                                {[
                                    { label: 'Urgent', value: TASK_PRIORITY.URGENT },
                                    { label: 'High', value: TASK_PRIORITY.HIGH },
                                    { label: 'Normal', value: TASK_PRIORITY.NORMAL },
                                    { label: 'Low', value: TASK_PRIORITY.LOW },
                                ].map((opt) => (
                                    <Box key={opt.value} onClick={() => setPriority(opt.value)} sx={chipSx(priority === opt.value)}>
                                        {opt.label}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Stack>
                </Collapse>
            </Box>

            {/* Submit ───────────────────────────────────────────────────────── */}
            <LoadingButton
                loading={saving}
                loadingPosition="start"
                startIcon={<Iconify icon="solar:bell-bold" width={16} height={16} />}
                onClick={handleSchedule}
                disabled={!dueAt || saving}
                fullWidth
                sx={{
                    bgcolor: '#00AB55',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    textTransform: 'none',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    py: 1,
                    '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                    '&:active': { bgcolor: '#017A3D' },
                    '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                }}
            >
                Schedule reminder
            </LoadingButton>
        </Box>
    );
};

ScheduleTaskReminder.propTypes = {
    /** The practice ID — used for the createTask endpoint and feature-flag check. */
    practiceId: PropTypes.number,
    /** Lead ID to attach the task to. */
    leadId: PropTypes.number,
    /** Full lead object from Redux (followLead). Used for auto-title and treatment resolution. */
    leadDetail: PropTypes.object,
    /**
     * Called (no args) after a task is created successfully. Used by the
     * attended popups to auto-select the "Reminder & Follow-up" outcome chip,
     * which unlocks the Finish button.
     */
    onTaskActivity: PropTypes.func,
    /** Called to close the parent popup after scheduling. */
    handleClose: PropTypes.func,
    /** Called to trigger a table/data reload after scheduling. */
    handleReloadLeads: PropTypes.func,
};

ScheduleTaskReminder.defaultProps = {
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onTaskActivity: null,
    handleClose: null,
    handleReloadLeads: null,
};

export default ScheduleTaskReminder;
